import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import EnhancedTable from '@common/components/Table/EnhancedTable'
import { HeadCell } from '@common/components/Table/table.types'
import { ReadTask } from '@common/config/api/client'
import { ROW_PER_PAGE_OPTIONS } from '@common/constants/table'
import { useSearch } from '@common/hooks/useSeach'
import { SPACING } from '@common/theme/spacing'
import { useReadTasksManagementHandlers } from '@features/reader_task_list_iam_view/pages/hooks/useReadTasksManagementHandlers'
import { Stack, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import EnhancedTableBodyMyTasks from '@reader_task_list_view/components/EnhancedTableBodyMyTasks'
import useGetReadDesignDetails from '@reader_task_list_view/hooks/useGetReadDesignDetails'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

interface ReaderTaskListTableMyTasksProps {
  subjectId?: string
  isRawDataEnabled?: boolean
  isOphthoEnabled?: boolean
}

const headCells: readonly HeadCell<ReadTask>[] = [
  { id: 'task_id', disablePadding: true, label: 'Task pseudo ID', disableSort: false },
  { id: 'blind_code', disablePadding: false, label: 'Blind code', disableSort: false },
  { id: 'task_type', disablePadding: false, label: 'Type', disableSort: false },
  { id: 'priority', disablePadding: false, label: 'Priority', disableSort: false },
  { id: 'created_at', disablePadding: false, label: 'Created at', disableSort: false },
  { id: 'due_date', disablePadding: false, label: 'Due date', disableSort: false },
  { id: 'status', disablePadding: false, label: 'Status', disableSort: false },
]

function EmptyReaderTasksTable({ searchValue }: { searchValue?: string }) {
  return (
    <Stack spacing={SPACING.minSpacing} alignItems="center" sx={{ py: SPACING.spacing2xl }}>
      <Typography variant="h6">{searchValue ? 'No matching tasks found' : 'No reader tasks available'}</Typography>
      <Typography variant="body2" color="text.secondary">
        {searchValue
          ? 'Try adjusting your search criteria or clear the search field'
          : 'There are currently no tasks assigned to you'}
      </Typography>
    </Stack>
  )
}

export default function ReaderTaskListTableMyTasks({
  subjectId,
  isRawDataEnabled,
  isOphthoEnabled,
}: ReaderTaskListTableMyTasksProps) {
  const { studyId } = useParams()
  const { value: searchValue } = useSearch()
  const { user } = useExtendedAuth()

  const {
    order,
    orderBy,
    page,
    paginationParams,
    data,
    error,
    isLoading,
    handleRequestSort,
    handlePageChange,
    handleRowsPerPageChange,
  } = useReadTasksManagementHandlers({
    category: 'my_tasks',
    studyId: studyId!,
    subjectId,
    searchValue,
  })

  const { data: readDesigns, isPending: isReadDesignDataLoading } = useGetReadDesignDetails(studyId!)

  // Determine if forced read is enabled
  const isForcedReadEnabled = useMemo(() => {
    if (isReadDesignDataLoading || !readDesigns) {
      return false
    }
    return readDesigns.some((design) => design.enable_forced_reads)
  }, [isReadDesignDataLoading, readDesigns])

  // Sort and filter tasks based on forced read flag
  const displayData = useMemo(() => {
    const tasks = data?.data?.items || []

    if (!isForcedReadEnabled || tasks.length === 0) {
      return tasks
    }

    // Sort tasks by priority (ascending) and then by created_at (ascending)
    const sortedTasks = [...tasks].sort((a, b) => {
      if (a.priority !== b.priority) {
        return (a.priority || 0) - (b.priority || 0)
      }

      // If priorities are the same, sort by created_at
      const dateA = new Date(a.created_at).getTime()
      const dateB = new Date(b.created_at).getTime()
      return dateA - dateB
    })

    // Return only the first element after sorting
    return [sortedTasks[0]]
  }, [data?.data?.items, isForcedReadEnabled])

  if (isLoading || isReadDesignDataLoading) {
    return <div>Loading...</div>
  }
  if (error) {
    return <div>Error: {error.message}</div>
  }

  const hasReaderTasks = displayData.length > 0

  return (
    <EnhancedTable<ReadTask>
      rows={displayData}
      rowCount={-1}
      headCells={headCells}
      order={order}
      orderBy={orderBy.split('.')[0] as keyof ReadTask}
      page={page}
      rowsPerPage={paginationParams.limit || 0}
      rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
      next_cursor={isForcedReadEnabled ? null : data?.data?.pagination.next_cursor}
      previous_cursor={isForcedReadEnabled ? null : data?.data?.pagination.previous_cursor}
      onRequestSort={handleRequestSort}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handleRowsPerPageChange}
    >
      {hasReaderTasks ? (
        <EnhancedTableBodyMyTasks
          rows={displayData}
          order={order}
          orderBy={orderBy}
          authedUserToken={user?.access_token}
          authedUserId={user?.profile.sub}
          isRawDataEnabled={isRawDataEnabled || false}
          isOphthoEnabled={isOphthoEnabled || false}
        />
      ) : (
        <TableBody>
          <TableRow>
            <TableCell colSpan={headCells.length}>
              <EmptyReaderTasksTable searchValue={searchValue} />
            </TableCell>
          </TableRow>
        </TableBody>
      )}
    </EnhancedTable>
  )
}
